import _ from 'lodash';
import { authorizeWallet, selectWallet } from '../paymentMethods';
import { PaymentError } from '../../lib/error';

import {
  getBraintreeInstance,
  getPaymentBreakdown,
  selectPaymentMethod,
  updatePaymentInfo,
} from '../../lib/wallets';

export const BUTTON_CONTAINER = '#PayPalButtonContainer';
export const PAYMENT_METHOD_ID = 'paypalonetouch';
export const PAYMENT_METHOD_NAME = 'PayPal';
export const ICON_FILENAME = 'payment-paypal-icon.svg';
export const AUTO_COMPLETE_PURCHASE = true;

let authorized = false;
let braintreeNonce;
let emailAddress;
let braintreePaymentInstance;

const buttonStyles = {
  layout: 'vertical',
  color: 'gold',
  shape: 'rect',
  label: 'paypal',
};

/** Formats the breakdown returned by the API to the shape needed by Braintree */
const formatPaymentBreakdown = (breakdown) => {
  const items = breakdown
    ?.filter((item) => item && item.label && (item.amount || item.isTotal))
    ?.map(
      ({
        amount,
        isTicket,
        isTotal,
        label,
        quantity,
      }) => ({
        quantity: 1,
        unitAmount: Math.abs(parseFloat(amount) / 100),
        name: (isTicket || (label === 'Convenience Fee')) ? `${label} x ${quantity}` : label,
        kind: amount < 0 ? 'credit' : 'debit',
        isTotal,
      }),
    );

  return {
    lineItems: items?.filter(({ isTotal }) => !isTotal)?.map((item) => _.omit(item, ['isTotal'])),
    total: items?.find(({ isTotal }) => isTotal)?.unitAmount,
  };
};

/**
 * createPayPalOrder
 *
 * Provides the button with needed info to launch the modal
 *
 * @param {Array.<Object>} lineItems - The line items for this transaction
 * @param {String} purchaseTotal - The cart total
 * @param {String} businessPropertyName - ie: Fandango, MovieTickets, Flixster
 */
const createPayPalOrder = ({
  lineItems,
  purchaseTotal,
  businessPropertyName,
}) => braintreePaymentInstance.createPayment({
  currency: 'USD',
  flow: 'checkout',
  amount: purchaseTotal,
  enableShippingAddress: false,
  displayName: businessPropertyName,
  lineItems,
});

/**
 * autoAuthorize
 *
 * Attempts to authorize the wallet after an approval by the user within the modal and a nonce has been obtained
 *
 * @param {String} purchaseTotal - The cart total
 * @param {boolean=} buyNow - Unique order of operations for Buy Now
 * @param {String} businessPropertyName - ie: Fandango, MovieTickets, Flixster
 * @param {function} autoAuthorizeError - The callback function used when authorization failed within selection
 * @param {function} autoAuthorizeSuccess - The callback function used when payment authorized within selection
 *
 * @throws {PaymentError}
 */

const autoAuthorize = ({
  purchaseTotal,
  buyNow,
  businessPropertyName,
  autoAuthorizeError,
  autoAuthorizeSuccess,
}) => authorizeWallet({
  walletId: PAYMENT_METHOD_ID,
  displayName: businessPropertyName,
  purchaseTotal,
  buyNow,
  success: () => autoAuthorizeSuccess({ autoCompletePurchase: AUTO_COMPLETE_PURCHASE }),
  error: autoAuthorizeError,
  cancel() {
    throw new PaymentError(
      `Authorizing wallet cancelled: ${PAYMENT_METHOD_ID}`,
      'Payment',
    );
  },
});

/**
 * getBraintreeNonce
 *
 * Tokenizes the user data retrieved after a successful login within the modal and approved payment method
 *
 * @param {Object} data - Data returned after a user has successfully signed into an account which is used to retrieve a nonce
 * @param {String} businessPropertyName - ie: Fandango, MovieTickets, Flixster
 * @param {String} purchaseTotal - The cart total
 * @param {boolean=} buyNow - Unique order of operations for Buy Now
 * @param {function} autoAuthorizeError - The callback function used when authorization failed within selection
 * @param {function} autoAuthorizeSuccess - The callback function used when payment authorized within selection
 *
 * @throws {PaymentError}
 */
const getBraintreeNonce = ({
  data,
  businessPropertyName,
  purchaseTotal,
  buyNow,
  autoAuthorizeError,
  autoAuthorizeSuccess,
}) => braintreePaymentInstance.tokenizePayment(data).then((payload) => {
  braintreeNonce = payload.nonce;
  emailAddress = Commerce.state.account.emailAddress || payload.details.email;

  if (!braintreeNonce) {
    throw new PaymentError(
      `Failed to retrieve a nonce: ${PAYMENT_METHOD_ID}`,
      'Payment');
  }

  autoAuthorize({
    businessPropertyName,
    purchaseTotal,
    buyNow,
    autoAuthorizeError,
    autoAuthorizeSuccess,
  });
}).catch((err) => {
  throw new PaymentError(err);
});

/**
 * renderPayPalButton
 *
 * PayPal Checkout v4 requires the SDK to render the button so this function sets up the configs
 *
 * @param {Object=} options
 * @param {function} options.autoAuthorizeError - The callback function used when authorization failed within selection
 * @param {function} options.autoAuthorizeSuccess - The callback function used when payment authorized within selection
 * @param {boolean=} options.buyNow - Unique order of operations for Buy Now
 *
 * @throws {PaymentError}
 */
export const renderPayPalButton = async ({
  autoAuthorizeError,
  autoAuthorizeSuccess,
  buyNow,
  onClick = () => {},
}) => {
  if (!braintreePaymentInstance) throw new PaymentError();
  const paymentBreakdown = await getPaymentBreakdown(PAYMENT_METHOD_ID, buyNow);
  const { lineItems, total } = formatPaymentBreakdown(paymentBreakdown);
  const purchaseTotal = total || Commerce.state.orderSummary.purchaseTotal || 0;

  return new Promise((resolve) => {
    braintreePaymentInstance
      .loadPayPalSDK({
        locale: 'en_US',
      })
      .then(() => {
        const { paypal } = window;
        const { businessPropertyName } = Commerce.state.config;

        setTimeout(() => {
          const buttonElement = document.querySelector(BUTTON_CONTAINER);
          if (buttonElement?.childNodes.length !== 0) {
            return resolve();
          }

          paypal.Buttons({
            createOrder: () => createPayPalOrder({ lineItems, purchaseTotal, businessPropertyName }),
            fundingSource: paypal.FUNDING.PAYPAL,
            style: buttonStyles,
            onApprove: (data) => getBraintreeNonce({
              data,
              businessPropertyName,
              purchaseTotal,
              buyNow,
              autoAuthorizeError,
              autoAuthorizeSuccess,
            }),
            onClick,
          }).render(BUTTON_CONTAINER);

          resolve();
        }, 150);
      }).catch((err) => {
        throw new PaymentError(err);
      });
  });
};

/**
 * init
 *
 * Initializes PayPal as a payment method.
 * This will always be a success as there are no device limitations for this payment method.
 *
 *
 * @returns {Boolean} Flag to indicate that PayPal has been initialized
 *   successfully and can be used as a payment method.
 */
export const init = () => true;

/**
 * prepare
 *
 * Prepares the Braintree instance for use downstream during authorization
 *
 * @async
 * @returns {String} The id of the PayPal payment method
 * @throws {PaymentError} Any errors thrown by `getBraintreeInstance` are simply propagated here
 */
export const prepare = async () => {
  braintreePaymentInstance = await getBraintreeInstance({ paymentMethodId: PAYMENT_METHOD_ID });
  return PAYMENT_METHOD_ID;
};

/**
 * select
 *
 * Saves PayPal as the selected payment method and prepares the Braintree
 * instance for use downstream during authorization
 *
 * @async
 * @param {Object=} options
 * @param {function=} options.autoAuthorizeError - The callback function used when authorization failed within selection
 * @param {function=} options.autoAuthorizeSuccess - The callback function used when payment authorized within selection
 * @throws {PaymentError} Any errors thrown by `selectPaymentMethod` are simply propagated here
 */
export const select = async ({ autoAuthorizeError, autoAuthorizeSuccess }) => {
  const response = await selectPaymentMethod({ paymentMethodId: PAYMENT_METHOD_ID, initializeBraintree: true });
  braintreePaymentInstance = response.braintreeInstance;

  if (autoAuthorizeSuccess) {
    await renderPayPalButton({ autoAuthorizeError, autoAuthorizeSuccess, buyNow: false });
  }
};

/**
 * authorize
 *
 * Authorizes PayPal as the payment method for the transaction and saves the Braintree nonce which is
 * used to complete the purchase.
 *
 * @throws {PaymentError}
 */
export const authorize = async ({ buyNow }) => {
  try {
    if (buyNow) {
      await selectWallet({ walletId: PAYMENT_METHOD_ID });
    }

    await updatePaymentInfo({ braintreeNonce, emailAddress, walletId: PAYMENT_METHOD_ID });
    authorized = true;
  } catch (err) {
    authorized = false;
    throw new PaymentError(err);
  }
};

export const isAuthorized = () => authorized;

export default PAYMENT_METHOD_ID;
